import { Link } from 'react-router-dom';
import { CheckIcon, GiftIcon } from 'lucide-react';
import { MediaView } from '../shared/MediaView';
import { ProductImage } from '../../ui/ProductImage';
import { useCatalog } from '../../contexts/CatalogContext';
import { useCartActions } from '../../contexts/StoreContext';
import { usePages, useSite } from '../../contexts/SiteContext';
import { unlockedIn } from '../../data/gifts';
import { shopLabels } from '../../data/shopLabels';
import { founder, salon } from '../../data/content';
import { euro } from '../../core/format/currency';
import { countSoldOut, isAllSoldOut, isSoldOut } from '../../core/rules/stock';
import { fill } from '../../utils/copy';
import { sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnPrice, btnPriceAmount, btnPriceLabel } from '../../ui/styles';
import type { Routine } from '../../core/types/catalog';
const primary = btnPrice;

/**
 * Het anker van de pagina: het complete protocol, op blush. Bewust geen protocolkaart in een
 * kader (dan stond het verhaal er twee keer), maar één uitgeschreven blok: het beeld blijft staan
 * (computer), rechts het verhaal, de producten in volgorde van gebruik, wat erbij zit en de
 * cadeaus die je met dit protocol vrijspeelt. Eén knop, met de prijs erin.
 */
export function RoutineAnchor({
  routine
}: {
  routine: Routine;
}) {
  const {
    routineTotals
  } = useCatalog();
  const {
    addRoutine
  } = useCartActions();
  const {
    items,
    price,
    months
  } = routineTotals(routine);
  const site = useSite();
  const pages = usePages();
  const gifts = unlockedIn(site.giftTiers, site.policy.freeShippingFrom, price);
  const soldOutCount = countSoldOut(items);
  const allSoldOut = isAllSoldOut(items);
  return <Section tone="blush" labelledBy="anchor-routine" contained={false} data-id="element-8279" data-mp="1j6568ph1qi6g">
      <div className="mx-auto grid max-w-content gap-10 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8280" data-mp="3pui5is6i44ds">
        <div className="lg:col-span-5" data-id="element-8281" data-mp="3kx06btiad3iz">
          <div className="lg:sticky lg:top-28" data-id="element-8282" data-mp="3ky1iy2ey9y2t">
            <MediaView media={{
            image: routine.image || founder.withProducts,
            alt: ''
          }} sizes="(min-width: 1024px) 38vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:aspect-[4/5]" data-id="element-8283" data-mp="1otlnqmyeaa1o" />
          </div>
        </div>

        <div className="lg:col-span-7" data-id="element-8284" data-mp="1xspbw5cf5fn1">
          <p className="text-[14px] text-ink" data-id="element-8285" data-mp="1xw3bj5tp7aqs">{fill(pages.routines.anchorEyebrow)}</p>
          <h2 id="anchor-routine" className={`mt-4 ${sectionHeading} text-ink`} data-id="element-8286" data-mp="22u4r3grx0rxi">
            {routine.name}
          </h2>
          <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-ink" data-id="element-8287" data-mp="2cr1685cbkcb1">{routine.story}</p>
          {routine.forWho && <p className="mt-3 max-w-xl text-[15px] leading-relaxed text-muted" data-id="element-8288" data-mp="2hhzpndsltqii">Voor wie: {routine.forWho.toLowerCase()}.</p>}

          {/* De volgorde is de boodschap: zo gebruik je het, van reinigen tot bescherming. */}
          <h3 className="mt-10 text-[14px] text-ink" data-id="element-8289" data-mp="2qj7buxhuj0w8">In volgorde van gebruik</h3>
          <ol className="mt-3 divide-y divide-ink/15 border-y border-ink/15" data-id="element-8290" data-mp="2vgzbyesn9tbi">
            {items.map((p, i) => <li key={p.id} className="flex items-center gap-4 py-3" data-id="element-8291" data-mp="35lpfgp3p55hu" data-mpi={i}>
                <span aria-hidden="true" className="w-5 shrink-0 type-name text-muted tabular-nums" data-id="element-8292" data-mp="30mmbl7zgd5bg">
                  {i + 1}
                </span>
                <ProductImage src={p.image} alt="" width={48} className="h-14 w-12 shrink-0 bg-shell p-1" data-id="element-8293" data-mp="2vnruqbl1pqtz" />
                <div className="min-w-0 flex-1" data-id="element-8294" data-mp="2rnhdu3vptg0c">
                  <p className="text-[12px] text-ink" data-id="element-8295" data-mp="3ocgofpjrutvp">{isSoldOut(p) ? shopLabels.soldOutShort : p.step}</p>
                  <Link to={`/webshop/${p.id}`} className="text-[15px] leading-snug text-ink underline-offset-4 hover:underline" data-id="element-8296" data-mp="3jckivlvfeue1">
                    {p.name}
                  </Link>
                </div>
                <span className={`text-[14px] tabular-nums text-muted ${isSoldOut(p) ? 'line-through' : ''}`} data-id="element-8297" data-mp="3albv156zyxtt">{euro(p.price)}</span>
              </li>)}
          </ol>

          <div className="mt-8 grid gap-8 sm:grid-cols-2" data-id="element-8298" data-mp="66kfpcq588hd">
            {routine.included.length > 0 && <div data-id="element-8299" data-mp="3mt5jw0mf280b">
                <h3 className="text-[14px] text-ink" data-id="element-8300" data-mp="3msndwc9ycff7">Kosteloos inbegrepen</h3>
                <ul className="mt-3 space-y-2" data-id="element-8301" data-mp="3rtb794lnl8cl">
                  {routine.included.map(inc => <li key={inc} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8302" data-mp="3dtillnozhibh">
                      <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8303" data-mp="3htu3ispyp4j9" />
                      {inc}
                    </li>)}
                </ul>
              </div>}
            {gifts.length > 0 && <div data-id="element-8304" data-mp="mqgzn3fa4gm">
                <h3 className="text-[14px] text-ink" data-id="element-8305" data-mp="na0v9cmv2xa">Daarbij ligt klaar</h3>
                <ul className="mt-3 space-y-2" data-id="element-8306" data-mp="4ntzyigrogm2">
                  {gifts.map(g => <li key={g.ruleId} className="flex items-start gap-2.5 text-[15px] leading-snug text-ink" data-id="element-8307" data-mp="sdobmwpcnz4q">
                      <GiftIcon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden="true" data-id="element-8308" data-mp="x44puj0zvos5" />
                      {g.title}
                    </li>)}
                </ul>
              </div>}
          </div>

          <div className="mt-10 max-w-md" data-id="element-8309" data-mp="2cv5rjyb3hht1">
            <p className="mb-3 text-[14px] text-muted" data-id="element-8310" data-mp="2rkbpliy751zc">
              Gaat {months} mee · dezelfde prijs als los, zonder giswerk
            </p>
            {allSoldOut ? <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={`${primary} justify-center`} data-id="element-8311" data-mp="2mnurbvxa978j">
                {shopLabels.soldOutShort} · vraag {founder.firstName} om een alternatief
                <span className="sr-only" data-id="element-8312" data-mp="1tz7emoqlnq6l">{shopLabels.viaWhatsApp}</span>
              </a> : <button type="button" onClick={() => addRoutine(routine.id)} className={primary} data-id="element-8313" data-mp="23v0lpsljurgz">
                <span className={btnPriceLabel} data-id="element-8314" data-mp="3k98wkpwyjccs">
                  Protocol in de mand<span className="sr-only" data-id="element-8315" data-mp="3k7for7alym37">: {routine.name}</span>
                </span>
                <span className={btnPriceAmount} data-id="element-8316" data-mp="3f9ny0glp960l">{euro(price)}</span>
              </button>}
            {soldOutCount > 0 && !allSoldOut && <p className="mt-3 text-[13px] leading-relaxed text-muted" data-id="element-8317" data-mp="3rcgufh0ih6it">{shopLabels.partlySoldOut(soldOutCount)}</p>}
          </div>
        </div>
      </div>
    </Section>;
}